import { useState, useEffect } from 'react'
import {
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  Languages,
  AlertTriangle,
  X
} from 'lucide-react'
import { categoryApi } from '../../lib/api'
import AdminNavHeader from '../../components/AdminNavHeader'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface Category {
  id: string
  name: string
  slug: string
  description?: string
  image_url?: string
  is_global: boolean
  parent_id?: string
  sort_order: number
  level: number
  path: string[]
  children_count: number
}

const AdminCategories = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showTranslationsModal, setShowTranslationsModal] = useState(false)
  const [language, setLanguage] = useState('es')

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image_url: '',
    parent_id: '',
    sort_order: 0
  })

  const [translations, setTranslations] = useState<any[]>([])
  const [currentTranslation, setCurrentTranslation] = useState({
    language: '',
    name: '',
    description: ''
  })

  useEffect(() => {
    loadCategories()
  }, [language])

  const loadCategories = async () => {
    const cacheKey = `admin_categories_${language}`
    
    // Intentar cargar desde cache primero
    const cachedCategories = getCacheStale<Category[]>(cacheKey)
    if (cachedCategories && cachedCategories.length > 0) {
      setCategories(cachedCategories)
      setLoading(false)
    }

    try {
      setLoading(true)
      setError('')
      const data = await categoryApi.getGlobalCategories(language)
      // Ensure data is always an array
      const categoriesData = Array.isArray(data) ? data : []
      setCategories(categoriesData)
      
      // Guardar en cache (1 hora de TTL - categorías cambian poco)
      setCache(cacheKey, categoriesData, 60 * 60 * 1000)
    } catch (err: any) {
      console.error('Error loading categories:', err)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response) && cachedCategories) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      setError('Error al cargar las categorías')
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCategory = async () => {
    try {
      await categoryApi.createGlobalCategory(formData)
      setShowCreateModal(false)
      resetForm()
      await loadCategories()
    } catch (err: any) {
      console.error('Error creating category:', err)
      setError(err.response?.data?.message || 'Error al crear la categoría')
    }
  }

  const handleUpdateCategory = async () => {
    if (!selectedCategory) return

    try {
      await categoryApi.updateGlobalCategory(selectedCategory.id, formData)
      setShowEditModal(false)
      resetForm()
      await loadCategories()
    } catch (err: any) {
      console.error('Error updating category:', err)
      setError(err.response?.data?.message || 'Error al actualizar la categoría')
    }
  }

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return

    try {
      await categoryApi.deleteGlobalCategory(selectedCategory.id)
      setShowDeleteModal(false)
      setSelectedCategory(null)
      await loadCategories()
    } catch (err: any) {
      console.error('Error deleting category:', err)
      setError(err.response?.data?.message || 'Error al eliminar la categoría')
    }
  }

  const loadTranslations = async (categoryId: string) => {
    try {
      const data = await categoryApi.getCategoryTranslations(categoryId)
      setTranslations(data)
    } catch (err: any) {
      console.error('Error loading translations:', err)
    }
  }

  const handleSaveTranslation = async () => {
    if (!selectedCategory) return

    try {
      await categoryApi.saveCategoryTranslation(
        selectedCategory.id,
        currentTranslation.language,
        {
          name: currentTranslation.name,
          description: currentTranslation.description
        }
      )
      await loadTranslations(selectedCategory.id)
      setCurrentTranslation({ language: '', name: '', description: '' })
    } catch (err: any) {
      console.error('Error saving translation:', err)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      image_url: '',
      parent_id: '',
      sort_order: 0
    })
    setSelectedCategory(null)
  }

  const openEditModal = (category: Category) => {
    setSelectedCategory(category)
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image_url: category.image_url || '',
      parent_id: category.parent_id || '',
      sort_order: category.sort_order
    })
    setShowEditModal(true)
  }

  const openDeleteModal = (category: Category) => {
    setSelectedCategory(category)
    setShowDeleteModal(true)
  }

  const openTranslationsModal = async (category: Category) => {
    setSelectedCategory(category)
    await loadTranslations(category.id)
    setShowTranslationsModal(true)
  }

  const toggleExpanded = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId)
    } else {
      newExpanded.add(categoryId)
    }
    setExpandedCategories(newExpanded)
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
  }

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name)
    }))
  }

  const renderCategoryTree = (categories: Category[], level = 0): React.ReactElement[] => {
    // Ensure categories is a valid array
    if (!Array.isArray(categories)) {
      return []
    }

    return categories
      .filter(cat => cat.level === level)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(category => (
        <div key={category.id}>
          <div className={`flex items-center justify-between p-3 hover:bg-gray-50 border-b border-gray-100 ${category.level > 0 ? 'ml-6' : ''}`}>
            <div className="flex items-center flex-1">
              {category.children_count > 0 ? (
                <button
                  onClick={() => toggleExpanded(category.id)}
                  className="mr-2 p-1 hover:bg-gray-200 rounded"
                >
                  {expandedCategories.has(category.id) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              ) : (
                <div className="w-8" />
              )}

              <div className="flex items-center">
                {category.image_url && (
                  <img
                    src={category.image_url}
                    alt={category.name}
                    className="w-8 h-8 rounded mr-3 object-cover"
                  />
                )}
                <div>
                  <h3 className="font-medium text-gray-900">{category.name}</h3>
                  <p className="text-sm text-gray-500">/{category.slug}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                Orden: {category.sort_order}
              </span>
              <button
                onClick={() => openTranslationsModal(category)}
                className="p-1 text-gray-400 hover:text-blue-600"
                title="Traducciones"
              >
                <Languages className="w-4 h-4" />
              </button>
              <button
                onClick={() => openEditModal(category)}
                className="p-1 text-gray-400 hover:text-blue-600"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => openDeleteModal(category)}
                className="p-1 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {expandedCategories.has(category.id) && category.children_count > 0 && (
            <div>
              {renderCategoryTree(categories, level + 1)}
            </div>
          )}
        </div>
      ))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <AdminNavHeader
        title="Categorías Globales"
        subtitle={`${categories.length} categorías registradas`}
      />
      
      {/* Language Selector and Create Button */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-white/30 px-4 py-3">
        <div className="flex items-center justify-between">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="px-4 py-2 bg-white/80 border border-gray-200/50 rounded-xl text-sm font-medium text-gray-700 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all duration-200"
          >
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-all active:scale-95"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Categoría
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          {renderCategoryTree(categories)}
        </div>
      </div>

      {/* Create Category Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Crear Nueva Categoría</h3>
              <form onSubmit={(e) => { e.preventDefault(); handleCreateCategory() }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Slug</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Descripción</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">URL de Imagen</label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Categoría Padre</label>
                    <select
                      value={formData.parent_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, parent_id: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Ninguna (Categoría Principal)</option>
                      {categories.filter(cat => !cat.parent_id).map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Orden</label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); resetForm() }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Crear
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && selectedCategory && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Categoría</h3>
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateCategory() }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Slug</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Descripción</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">URL de Imagen</label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Categoría Padre</label>
                    <select
                      value={formData.parent_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, parent_id: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Ninguna (Categoría Principal)</option>
                      {categories.filter(cat => !cat.parent_id && cat.id !== selectedCategory.id).map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Orden</label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowEditModal(false); resetForm() }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    Actualizar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCategory && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Eliminar Categoría</h3>
              <p className="text-sm text-gray-600 mb-4">
                ¿Estás seguro de que quieres eliminar la categoría "{selectedCategory.name}"?
                Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowDeleteModal(false); setSelectedCategory(null) }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteCategory}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Translations Modal */}
      {showTranslationsModal && selectedCategory && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Traducciones - {selectedCategory.name}
              </h3>

              {/* Existing Translations */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Traducciones Existentes</h4>
                <div className="space-y-2">
                  {translations.map((translation: any) => (
                    <div key={translation.language_code} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <span className="font-medium">{translation.language_code.toUpperCase()}</span>
                        <span className="ml-2 text-gray-600">{translation.name}</span>
                      </div>
                      <button
                        onClick={() => categoryApi.deleteCategoryTranslation(selectedCategory.id, translation.language_code)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add New Translation */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Agregar Traducción</h4>
                <div className="space-y-3">
                  <select
                    value={currentTranslation.language}
                    onChange={(e) => setCurrentTranslation(prev => ({ ...prev, language: e.target.value }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar idioma</option>
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="de">Deutsch</option>
                    <option value="it">Italiano</option>
                    <option value="pt">Português</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Nombre traducido"
                    value={currentTranslation.name}
                    onChange={(e) => setCurrentTranslation(prev => ({ ...prev, name: e.target.value }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <textarea
                    placeholder="Descripción traducida"
                    value={currentTranslation.description}
                    onChange={(e) => setCurrentTranslation(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleSaveTranslation}
                    disabled={!currentTranslation.language || !currentTranslation.name}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    Agregar Traducción
                  </button>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => { setShowTranslationsModal(false); setSelectedCategory(null); setTranslations([]) }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminCategories
